import axios, { AxiosInstance } from 'axios';
import type {
  BackendPet,
  BackendPage,
  SearchPetsParams,
} from '../types';

const baseURL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:8080/api';

/**
 * Serialize query params as repeated bare keys for arrays (`regimes=FAT&regimes=FAD`).
 * Axios 1.x defaults to bracketed keys (`regimes[]=FAT`), which Spring's
 * `@RequestParam List<Regime>` will not bind. Scalars pass through unchanged;
 * undefined/null are skipped.
 */
export function serializeParams(params: Record<string, unknown>): string {
  const parts: string[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const append = (v: unknown) =>
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
    if (Array.isArray(value)) value.forEach(append);
    else append(value);
  });
  return parts.join('&');
}

const client: AxiosInstance = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: { serialize: serializeParams },
});

/**
 * GET /api/pets/search — paginated. The backend accepts a wide query API
 * (gender, breeds, energy, temperaments, goodWith, etc.) but the public site
 * only exposes type, age band (LifeStage via `ageBand`), and regime,
 * so we narrow the call surface.
 */
export async function searchPets(
  params: SearchPetsParams = {},
  signal?: AbortSignal,
): Promise<BackendPage<BackendPet>> {
  const cleaned = stripEmpty(params);
  const { data } = await client.get<BackendPage<BackendPet>>('/pets/search', {
    params: cleaned,
    signal,
  });
  return data;
}

/**
 * GET /api/pets/{id}. Returns 404 when missing — caller should map the
 * AxiosError status to a notFound flag (see useFetchPet).
 */
export async function getPetById(
  id: string,
  signal?: AbortSignal,
): Promise<BackendPet> {
  const { data } = await client.get<BackendPet>(`/pets/${encodeURIComponent(id)}`, {
    signal,
  });
  return data;
}

function stripEmpty<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach((k) => {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') {
      out[k] = v;
    }
  });
  return out;
}
